import TemplatesApi from './Templates.api';

/**
 * Debounce utility with cancel support.
 * @param {Function} func - The function to debounce.
 * @param {number} delay - The delay in milliseconds.
 * @returns {Function} The debounced function with a cancel method.
 */
export const debounce = (func, delay) => {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };

  debounced.cancel = () => {
    clearTimeout(timeoutId);
  };

  return debounced;
};

/**
 * Fetches topics and templates from the API.
 * @returns {Promise<{topics: Array, templates: Array}>} The fetched data.
 */
export const fetchFiltersAndTemplates = async () => {
  const [topicsResponse, templatesResponse] = await Promise.all([
    TemplatesApi.getTopics(),
    TemplatesApi.getTemplates(),
  ]);
  return { topics: topicsResponse, templates: templatesResponse };
};

/**
 * Deletes a topic by ID.
 * @param {string} topicId - The ID of the topic to delete.
 * @throws {Error} If the deletion fails.
 */
export const deleteTopic = async (topicId) => {
  await TemplatesApi.deleteTopic(topicId);
};

/**
 * Deletes a subtopic by ID.
 * @param {string} subtopicId - The ID of the subtopic to delete.
 * @throws {Error} If the deletion fails.
 */
export const deleteSubtopic = async (subtopicId) => {
  await TemplatesApi.deleteSubtopic(subtopicId);
};

/**
 * Filters templates based on provided filters.
 * @param {Array} templates - The full list of templates.
 * @param {Object} filters - The filter criteria.
 * @returns {Array} The filtered templates.
 */
export const filterTemplates = (templates, filters) => {
  let filtered = templates;
  if (filters.postType && filters.postType !== 'all') {
    filtered = filtered.filter(
      (template) => template.category === filters.postType
    );
  }
  if (filters.topic && filters.topic !== 'all') {
    filtered = filtered.filter(
      (template) => String(template.topic?.id ?? '') === String(filters.topic)
    );
  }
  if (filters.subtopic && filters.subtopic !== 'all') {
    filtered = filtered.filter(
      (template) =>
        String(template.subtopic?.id ?? '') === String(filters.subtopic)
    );
  }
  return filtered;
};

/**
 * Searches templates by message content or template id (substring match).
 * @param {Array} templates - The list of templates to search.
 * @param {string} searchQuery - The search term.
 * @returns {Array} The filtered templates.
 */
export const searchTemplates = (templates, searchQuery = '') => {
  const normalizedQuery = normalizeString(searchQuery.trim());
  return templates.filter((template) => {
    const messageMatch = normalizeString(template.message).includes(
      normalizedQuery
    );
    const idMatch = normalizeString(template.id).includes(normalizedQuery);
    return messageMatch || idMatch;
  });
};

/**
 * Sorts templates by created_time descending (newest first).
 * Templates without created_time are listed last.
 * @param {Array} templates - The list of templates to sort.
 * @returns {Array} Templates sorted newest first.
 */
export const sortTemplatesByCreatedTime = (templates) => {
  return [...templates].sort((a, b) => {
    const aTime = Date.parse(a.created_time);
    const bTime = Date.parse(b.created_time);
    const aValue = Number.isNaN(aTime) ? 0 : aTime;
    const bValue = Number.isNaN(bTime) ? 0 : bTime;

    return bValue - aValue;
  });
};

/**
 * Applies list filters, optional search, and newest-first ordering for display.
 * @param {Array} templates - Full template list from the API.
 * @param {Object} filters - Applied post type / topic / subtopic filters.
 * @param {string} searchQuery - Optional message or id search term.
 * @returns {Array} Templates ready to render in the list.
 */
export const getDisplayedTemplates = (templates, filters, searchQuery = '') => {
  const filtered = filterTemplates(templates, filters);
  const searched = searchQuery
    ? searchTemplates(filtered, searchQuery)
    : filtered;

  return sortTemplatesByCreatedTime(searched);
};

/**
 * Normalizes a string for case-insensitive, accent-insensitive search.
 * @param {string} str - The string to normalize.
 * @returns {string} The normalized string.
 */
const normalizeString = (str = '') => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Gets the count of active templates for a topic or subtopic with an end_date in the future or no end_date (evergreen).
 * @param {Array} templates - The full list of templates.
 * @param {string} id - The topic or subtopic ID.
 * @param {string} type - 'topic' or 'subtopic'.
 * @returns {number} The number of active templates.
 */
export const getActiveTemplateCount = (templates, id, type) => {
  const now = new Date();
  return templates.filter((template) => {
    const isMatching =
      type === 'topic'
        ? template.topic?.id === id
        : template.subtopic?.id === id;
    const endDate = template.end_date ? new Date(template.end_date) : null;
    return isMatching && (!endDate || endDate > now);
  }).length;
};

/**
 * Ends all active templates for a given topic or subtopic by setting end_date to now.
 * @param {Array} templates - The full list of templates.
 * @param {string} id - The topic or subtopic ID.
 * @param {string} type - 'topic' or 'subtopic'.
 * @returns {Promise<void>} - Resolves when templates are ended.
 */
export const endActiveTemplatesForTopicOrSubtopic = async (
  templates,
  id,
  type
) => {
  const activeTemplateIds = templates
    .filter((template) => {
      const isMatching =
        type === 'topic'
          ? template.topic?.id === id
          : template.subtopic?.id === id;
      const endDate = template.end_date ? new Date(template.end_date) : null;
      return isMatching && (!endDate || endDate > new Date());
    })
    .map((template) => template.id);

  if (activeTemplateIds.length === 0) {
    throw new Error('No active templates to end.');
  }

  await TemplatesApi.endTemplates(activeTemplateIds);
};
